// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import PropertySetupScreenFooterTemplate from '@front/templates/PropertySetupScreenFooterTemplate.html';
import PropertySetupScreenTemplate from '@front/templates/PropertySetupScreenTemplate.html';
import TerceraBaseWindowTemplate from '@front/templates/TerceraBaseWindowTemplate.html';
import { HtmlScroll } from '@components/webdesktop/elements/Scroll/HtmlScroll';
import { InsDefSettings } from '@shared/commons/cache/InstrumentDefaults';
import { Connection } from '@shared/commons/Connection';
import { DataCache } from '@shared/commons/DataCache';
import { DynProperty } from '@shared/commons/DynProperty';
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Resources } from '@shared/localizations/Resources';
import { ChartWindow } from '../elements/ChartWindow';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { PairColorPicker } from '../elements/PairColorPicker';
import { TrioColorPicker } from '../elements/TrioColorPicker';
import { TrioColorGradient } from '../elements/TrioColorGradient';
import { TerceraBoolNumeric } from '../elements/TerceraBoolNumeric';
import { TerceraButton } from '../elements/TerceraButton';
import { TerceraButtonColorPicker } from '../elements/TerceraButtonColorPicker';
import { CheckBoxEvents, TerceraCheckBox } from '../elements/TerceraCheckBox';
import { TerceraCheckBoxEvent } from '../elements/TerceraCheckBoxEvent';
import { TerceraColorStyleWidthPicker } from '../elements/TerceraColorStyleWidthPicker';
import { TerceraComboBox, TerceraComboBoxEvents } from '../elements/TerceraComboBox';
import { TerceraFontPicker } from '../elements/TerceraFontPicker';
import { TerceraLabel } from '../elements/TerceraLabel';
import { TerceraLineStyleComboBox } from '../elements/TerceraLineStyleComboBox';
import { TerceraNumeric, TerceraNumericEvents } from '../elements/TerceraNumeric';
import { TerceraPropertyGroupSeparator } from '../elements/TerceraPropertyGroupSeparator';
import { TerceraRadioComboItemGroup } from '../elements/TerceraRadioComboItemGroup';
import { TerceraRangeControl } from '../elements/TerceraRangeControl';
import { TerceraSeparator } from '../elements/TerceraSeparator';
import { TerceraTextBox, TextBoxEvents } from '../elements/TerceraTextBox';
import { TerceraTIFComboBox } from '../elements/TerceraTIFComboBox';
import { TerceraWidthComboBox } from '../elements/TerceraWidthComboBox';
import { CreateAlertNotificationSelector } from '../trading/alerts/CreateAlertNotificationSelector';
import { MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { TerceraMessageBox } from './TerceraMessageBox';
import { TerceraWindowBase } from './TerceraWindowBase';
import { TypesManagerScreen } from './TypesManagerScreen';
import { TerceraGroupPanel } from '../elements/TerceraGroupPanel';
import { SoundManager } from '@shared/utils/SoundManager';
import { TerceraLookupEvents } from '../elements/Lookup/TerceraLookup';
import { AccountSelector } from '../elements/AccountSelector';
import { TerceraInstrumentLookup } from '../elements/Lookup/TerceraInstrumentLookup';
import { TerceraNumericWithRefreshButton } from '../elements/TerceraNumericWithRefreshButton';
import { TerceraButtonEvents } from '@shared/utils/Enums/ButtonEnums';
import { DynPropertyGroupComparer } from './DynPropertyGroupComparer';
import { HotkeysSettingsContainer } from '../elements/Hotkey/HotkeysSettingsContainer';
import { LineStyleUtils } from '@shared/commons/UtilsClasses/LineStyleUtils';
export class PropertySetupScreen extends TerceraWindowBase {
    constructor() {
        super();
        this.lastSelectedGroup = null;
        this.visibleControls = [];
        this.sortedProperties = null;
        this.loggingAllowed = null;
        this.onConnectionStateChange = () => {
            var _a;
            const normal = Connection.isConnectionCorrect();
            const len = this.visibleControls.length;
            const tt = Resources.getResource('dynProperty.inactive.tooltip');
            for (let i = 0; i < len; i++) {
                const control = this.visibleControls[i];
                let mytt = '';
                if ((_a = control.tag) === null || _a === void 0 ? void 0 : _a.tooltip) {
                    mytt = control.tag.tooltip;
                }
                if (control === null || control === void 0 ? void 0 : control.set) {
                    control.set({
                        enabled: normal,
                        tooltip: normal ? mytt : tt
                    });
                }
            }
            if (this.loggingAllowed) {
                void this.set({ enabledBtn: normal }); // #111602
            }
        };
        this.onCallerClosed = this.onCallerClosed.bind(this);
    }
    getType() {
        return 'PropertySetupScreen';
    }
    oncomplete() {
        super.oncomplete();
        this.scroll = HtmlScroll.addScroll(this.find('.js-right-panel'), 'xy', {
            scroll: () => {
                hideContextMenu();
            }
        });
        Connection.subscribeOnConnectStateChange(this.onConnectionStateChange);
        this.localize();
        this.center();
        this.loggingAllowed = DataCache.AllowLoggingChanges();
        this.oldProperties = this.arrayProperties.map((prop) => prop.serialize()); /* Save initial property values for change detection #124682 */
    }
    localize() {
        void this.set({
            okBtnText: Resources.getResource('screen.properties.ok'),
            okBtnTooltip: Resources.getResource('screen.properties.ok.ToolTip'),
            cancelBtnText: Resources.getResource('screen.properties.cancel'),
            cancelBtnTooltip: Resources.getResource('screen.properties.cancel.ToolTip')
        });
    }
    PopulateProperties(properties) {
        try {
            if (properties === null) {
                return;
            }
            // Клонируем свойтсва
            this.arrayProperties = DynProperty.CloneDynProperties(properties);
            this.arrayProperties.sort(DynPropertyGroupComparer.Compare);
            // Заполняем дерево
            this.FillTree();
            // Обновляем связи для всех проперти
            for (let i = 0, max = this.arrayProperties.length; i < max; i++) {
                const p = this.arrayProperties[i];
                if (p.assignedProperty) {
                    DynProperty.UpdatePropertyRelation(this.arrayProperties, p);
                }
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
    }
    FillTree() {
        const properties = this.arrayProperties;
        let len = properties.length;
        const groups = {};
        for (let i = 0; i < len; i++) {
            // sorting
            const curProp = properties[i];
            let curPropGroup = curProp.group;
            if (curPropGroup === '') {
                curPropGroup = DynProperty.PARAM_GROUP;
            }
            if (groups[curPropGroup] !== undefined) {
                groups[curPropGroup].push(curProp);
            }
            else {
                groups[curPropGroup] = [];
                groups[curPropGroup].push(curProp);
            }
        }
        const groupNames = Object.keys(groups);
        // Removes 'hidden' group from visible groups.
        const hiddenGr = groupNames.indexOf(DynProperty.HIDDEN_GROUP);
        if (hiddenGr !== -1) {
            groupNames.splice(hiddenGr, 1);
        }
        const fibonacciParamsGr = groupNames.indexOf(DynProperty.FIBONACCI_PARAMETERS_GROUP);
        if (fibonacciParamsGr !== -1) {
            groupNames.splice(fibonacciParamsGr, 1);
        }
        len = groupNames.length;
        const evt = new CustomEvent();
        evt.Subscribe(this.switchBtnClick, this);
        for (let i = 0; i < len; i++) {
            const groupName = groupNames[i];
            if (groupName === DynProperty.HIDDEN_GROUP || groupName === DynProperty.FIBONACCI_PARAMETERS_GROUP) {
                continue;
            }
            const groupLocStrings = DynProperty.getLocalizedGroupStrings(groupName);
            if (Resources.isHidden(groupLocStrings.name)) {
                continue;
            }
            if (groupName === DynProperty.SEPARATOR_GROUP1 || groupName === DynProperty.SEPARATOR_GROUP2) {
                const separator = new TerceraSeparator();
                this.Controls.leftPanel.addControl(separator);
                continue;
            }
            const btn = new TerceraButton();
            void btn.set({
                text: groupLocStrings.name,
                tooltip: groupLocStrings.tooltip,
                terceraButtonStyle: 'js-settings-switch',
                SVG: true,
                canCheck: true,
                name: groupName
            });
            btn.onBtnClick = evt;
            btn.btnInGroup = true;
            this.Controls.leftPanel.addControl(btn);
        }
        this.sortedProperties = groups;
        this.selectGroup(groupNames[0]);
        this.initLastBtn(groupNames[0]);
        // Hides left panel.
        // this.set('oneGroup', groupNames.length === 1);
    }
    // Основной метод: заполнение правой панели отфильтрованными свойствами
    FillGrid(properties) {
        this.visibleControls.forEach((control) => control.dispose()); /* clear */
        this.visibleControls = [];
        const usedSeparatorsDict = {};
        const isConnectionCorrect = Connection.isConnectionCorrect();
        const notCorrectConnectionTT = Resources.getResource('dynProperty.inactive.tooltip');
        let gr;
        let label;
        let ractiveControl;
        for (let max = properties.length, i = 0; i < max; i += 1) {
            const prop = properties[i];
            if (!prop.visible || Resources.isHidden(prop.localizationKey))
                continue;
            gr = null;
            label = null;
            ractiveControl = null;
            // Adding separator groups.
            const separatorGroup = prop.separatorGroup.replace(/#.*#/, '');
            if (separatorGroup && !usedSeparatorsDict[separatorGroup]) {
                usedSeparatorsDict[separatorGroup] = true;
                const separator = new TerceraPropertyGroupSeparator();
                void separator.set({ text: separatorGroup });
                this.Controls.rightPanel.addControl(separator);
                this.visibleControls.push(separator);
            }
            switch (prop.type) {
                case DynProperty.SEPARATOR: {
                    const separator = new TerceraSeparator();
                    this.Controls.rightPanel.addControl(separator);
                    this.visibleControls.push(separator);
                    continue;
                }
                case DynProperty.GROUP_SEPARATOR: {
                    const separator = new TerceraPropertyGroupSeparator();
                    void separator.set({ text: prop.GetLocalizedName() });
                    this.Controls.rightPanel.addControl(separator);
                    this.visibleControls.push(separator);
                    continue;
                }
                case DynProperty.INSTRUMENTS_DEFAULTS:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: Resources.getResource('TradeDefaultManager.Types manager')
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraButton();
                    ractiveControl.set({
                        terceraButtonStyle: 'js-button-gray25',
                        text: Resources.getResource('TradeDefaultManager.Set defaults')
                    });
                    ractiveControl.on(TerceraButtonEvents.onClick, PropertySetupScreen.onInstrumentDefaultsButtonClick);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.STRING:
                case DynProperty.PASSWORD: {
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraTextBox();
                    ractiveControl.set({
                        text: prop.value
                    });
                    if (prop.type === DynProperty.PASSWORD) {
                        ractiveControl.set({
                            inputType: 'password'
                        });
                    }
                    ractiveControl.on(TextBoxEvents.TextChanged, this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                }
                case DynProperty.BOOLEAN:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    ractiveControl = new TerceraCheckBox();
                    ractiveControl.set({
                        text: prop.GetLocalizedName(),
                        checked: prop.value,
                        enabled: prop.enabled
                    });
                    ractiveControl.on(CheckBoxEvents.CheckedStateChange, this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl);
                    break;
                case DynProperty.BOOLEAN_EVT:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    ractiveControl = new TerceraCheckBoxEvent();
                    ractiveControl.set({
                        text: prop.GetLocalizedName(),
                        checked: prop.value,
                        enabled: prop.enabled
                    });
                    ractiveControl.on(CheckBoxEvents.CheckedStateChange, this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    ractiveControl.BeforeChangeEvent = prop.BeforeChangeEvent;
                    gr.addControl(ractiveControl);
                    break;
                case DynProperty.RADIOBUTTON_COMBOITEM:
                    label = null;
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    ractiveControl = new TerceraRadioComboItemGroup();
                    ractiveControl.set({
                        isHorisontal: true,
                        spaceBetweenItems: true
                    });
                    ractiveControl.setItems(prop.value, prop.objectVariants);
                    ractiveControl.SelectedValueChanged.Subscribe(this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl);
                    break;
                case DynProperty.RADIOBUTTON_COMBOITEM_WITH_LABEL:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: Resources.getResource(prop.localizationKey)
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraRadioComboItemGroup();
                    ractiveControl.set({
                        isHorisontal: true,
                        spaceBetweenItems: true
                    });
                    ractiveControl.setItems(prop.value, prop.objectVariants);
                    ractiveControl.SelectedValueChanged.Subscribe(this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.BOOL_NUMERIC:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = null;
                    ractiveControl = new TerceraBoolNumeric();
                    ractiveControl.set({
                        boolNumericData: prop.value,
                        labelText: prop.GetLocalizedName(),
                        minValue: prop.minimalValue,
                        maxValue: prop.maximalValue,
                        step: prop.increment,
                        decimalPrecision: prop.decimalPlaces
                    });
                    ractiveControl.BoolNumericDataChanged.Subscribe(this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl);
                    break;
                case DynProperty.INTEGER:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraNumeric();
                    ractiveControl.set({
                        minValue: prop.minimalValue,
                        maxValue: prop.maximalValue,
                        step: 1,
                        decimalPrecision: 0
                    });
                    ractiveControl.set({ value: prop.value });
                    ractiveControl.on(TerceraNumericEvents.ValueChanged, this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.DOUBLE:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraNumeric();
                    ractiveControl.set({
                        minValue: prop.minimalValue,
                        maxValue: prop.maximalValue,
                        step: prop.increment,
                        decimalPrecision: prop.decimalPlaces
                    });
                    ractiveControl.set({ value: prop.value });
                    ractiveControl.on(TerceraNumericEvents.ValueChanged, this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.COMBOBOX_COMBOITEM_TIF:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraTIFComboBox();
                    ractiveControl.set({
                        text: prop.GetLocalizedName(),
                        items: prop.objectVariants
                    });
                    ractiveControl.Tag = prop;
                    ractiveControl.setItembyValue(prop.value.tif.value);
                    ractiveControl.set({ gtdDate: prop.value.date });
                    ractiveControl.on(TerceraComboBoxEvents.ValueChange, this.private_ControlValueChanged.bind(this));
                    // ractiveControl.dateChanged.Subscribe(this.private_ControlValueChanged, this);
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.COMBOBOX:
                case DynProperty.CSSTYLE:
                case DynProperty.LINEWIDTH:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    if (DynProperty.LINEWIDTH === prop.type) {
                        ractiveControl = new TerceraWidthComboBox();
                        const widthItems = LineStyleUtils.createWidthItems();
                        ractiveControl.set({ items: widthItems });
                    }
                    else if (DynProperty.CSSTYLE === prop.type) {
                        ractiveControl = new TerceraLineStyleComboBox();
                    }
                    else {
                        ractiveControl = new TerceraComboBox();
                        ractiveControl.set('items', prop.objectVariants);
                    }
                    ractiveControl.set({
                        text: prop.GetLocalizedName()
                    });
                    ractiveControl.Tag = prop;
                    ractiveControl.setItembyValue(prop.value);
                    ractiveControl.on(TerceraComboBoxEvents.ComboItemClicked, this.private_ControlItemChanged.bind(this));
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.ALERT_NOTIFICATION_SELECTOR:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    ractiveControl = new CreateAlertNotificationSelector();
                    ractiveControl.set({ selectedValue: prop.value });
                    ractiveControl.Tag = prop;
                    ractiveControl.ValueChanged.Subscribe(this.private_ControlValueChanged);
                    gr.addControl(ractiveControl);
                    break;
                case DynProperty.BOOLEAN_SOUND: {
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    ractiveControl = new TerceraCheckBox();
                    ractiveControl.set({
                        checked: prop.value,
                        enabled: prop.enabled
                    });
                    ractiveControl.on(CheckBoxEvents.CheckedStateChange, this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl);
                    const sound = new TerceraButton();
                    void sound.set({
                        terceraButtonStyle: 'js-sound-test',
                        enabled: prop.enabled
                    });
                    sound.on(TerceraButtonEvents.onClick, this.private_OnSoundPlay);
                    sound.Tag = prop;
                    gr.addControl(sound);
                    this.visibleControls.push(sound);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    // #51504
                    ractiveControl.observe('enabled', this.onBooleanSoundControlEnabledChanged, { context: sound });
                    break;
                }
                case DynProperty.INSTRUMENT:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraInstrumentLookup();
                    ractiveControl.set({
                        mayContainTrees: true
                    });
                    ractiveControl.Tag = prop;
                    ractiveControl.selectItem(prop.value);
                    ractiveControl.on(TerceraLookupEvents.SelectedItemChanged, this.private_InstrumentValueChanged);
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.ACCOUNT:
                    if (DataCache.EnableForceLinkingByAccount()) {
                        continue;
                    }
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new AccountSelector();
                    ractiveControl.Tag = prop;
                    ractiveControl.on(TerceraLookupEvents.SelectedItemChanged, this.private_AccountValueChanged);
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.COLOR:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraButtonColorPicker();
                    ractiveControl.set({
                        coloringButtonColor: prop.value
                    });
                    ractiveControl.ColorChanged.Subscribe(this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.PAIR_COLOR:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new PairColorPicker();
                    ractiveControl.set({
                        pairColor: prop.value
                    });
                    ractiveControl.PairColorChanged.Subscribe(this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.TRIO_COLOR:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TrioColorPicker();
                    ractiveControl.set({
                        trioColor: prop.value
                    });
                    ractiveControl.TrioColorChanged.Subscribe(this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.TRIO_COLOR_GRADIENT:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TrioColorGradient();
                    ractiveControl.set({
                        value: prop.value
                    });
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.COLOR_STYLE_WIDTH:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = null;
                    ractiveControl = new TerceraColorStyleWidthPicker();
                    if (isValidArray(prop.objectVariants)) {
                        ractiveControl.set({ styleItems: prop.objectVariants });
                    }
                    ractiveControl.set({
                        useStyleProperties: prop.objectVariants ? !!prop.objectVariants.useStyleProperties : false,
                        colorStyleWidth: prop.value,
                        labelText: prop.GetLocalizedName()
                    });
                    ractiveControl.ColorStyleWidthChanged.Subscribe(this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl);
                    break;
                case DynProperty.FONT:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraFontPicker();
                    ractiveControl.set({
                        font: prop.value
                    });
                    ractiveControl.FontChanged.Subscribe(this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl);
                    break;
                case DynProperty.CHART_WINDOW: {
                    gr = new TerceraGroupPanel();
                    gr.set('subclass', 'chart-window-group');
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new ChartWindow();
                    ractiveControl.SetMinimum(prop.minimalValue);
                    ractiveControl.SetMaximum(prop.maximalValue);
                    ractiveControl.SetValue(prop.value);
                    ractiveControl.ValueChanged.Subscribe(this.private_ControlValueChanged, this);
                    const setValue = ractiveControl.GetValue();
                    if (prop.value !== setValue) {
                        ractiveControl.SetValue(setValue);
                    }
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                }
                case DynProperty.RANGECONTROL:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraRangeControl();
                    ractiveControl.Tag = prop;
                    ractiveControl.setInitValue(prop.value);
                    ractiveControl.RangeValueChanged.Subscribe(this.private_ControlValueChanged, this);
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.DOUBLE_WITH_REFRESH_BUTTON:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    ractiveControl = new TerceraNumericWithRefreshButton();
                    ractiveControl.set({
                        minValue: prop.minimalValue,
                        maxValue: prop.maximalValue,
                        step: prop.increment,
                        decimalPrecision: prop.decimalPlaces,
                        refreshCallback: prop.callback
                    });
                    ractiveControl.set({ value: prop.value });
                    ractiveControl.on(TerceraNumericWithRefreshButton.Events.ValueChanged, this.private_ControlValueChanged);
                    ractiveControl.Tag = prop;
                    gr.addControl(ractiveControl, true);
                    break;
                case DynProperty.HOTKEY_SETTINGS_CONTAINER:
                    ractiveControl = new HotkeysSettingsContainer();
                    ractiveControl.HotkeyValueChanged.Subscribe(this.private_HotkeyValueChanged, this);
                    void this.set({ isContainerScreen: true });
                    this.Controls.rightPanel.addControl(ractiveControl);
                    break;
                default:
                    gr = new TerceraGroupPanel();
                    this.Controls.rightPanel.addControl(gr);
                    label = new TerceraLabel();
                    label.set({
                        text: prop.GetLocalizedName()
                    });
                    gr.addControl(label);
                    this.visibleControls.push(label);
                    continue;
            }
            // update property relation
            if (ractiveControl) {
                ractiveControl.ownerPanel = this;
            }
            if (label) {
                this.visibleControls.push(label);
            }
            if (gr) {
                this.visibleControls.push(gr);
            }
            if (ractiveControl) {
                // TODO. Remove previous enabled assignments.
                ractiveControl.set('enabled', prop.enabled);
                this.visibleControls.push(ractiveControl);
            }
            if (prop.tooltip) {
                ractiveControl.set({ tooltip: prop.tooltip });
            }
            if (!isConnectionCorrect) {
                ractiveControl.set({ tooltip: notCorrectConnectionTT, enabled: false });
            }
            prop.AssignedControl = ractiveControl;
        }
    }
    // #region DynProperty value changed
    private_UpdatePropertyRelation(sender, tag) {
        var _a;
        const scr = sender.ownerPanel;
        if ((_a = tag.assignedProperty) === null || _a === void 0 ? void 0 : _a.length) {
            DynProperty.UpdatePropertyRelation(scr.arrayProperties, tag);
            scr.selectGroup(scr.lastSelectedGroup.get('name'));
        }
    }
    private_HotkeyValueChanged(ractiveControl, item, value) {
        if (isNullOrUndefined(ractiveControl) || isNullOrUndefined(item)) {
            return;
        }
        const propName = 'property.Hotkeys.' + item.ActionName;
        const prop = DynProperty.getPropertyByName(this.arrayProperties, propName);
        prop.value = value;
        ractiveControl.ownerPanel.private_UpdatePropertyRelation(ractiveControl, prop);
    }
    private_ControlValueChanged(context, value) {
        var _a;
        const sender = (_a = context.ractive) !== null && _a !== void 0 ? _a : context;
        const tag = sender.Tag;
        tag.value = value;
        sender.ownerPanel.private_UpdatePropertyRelation(sender, tag);
    }
    private_ControlItemChanged(context, item) {
        this.private_ControlValueChanged(context, item.tag);
    }
    private_AccountValueChanged(context, value) {
        var _a;
        const sender = (_a = context.ractive) !== null && _a !== void 0 ? _a : context;
        const tag = sender.Tag;
        tag.value = value.AcctNumber;
        sender.ownerPanel.private_UpdatePropertyRelation(sender, tag);
    }
    private_InstrumentValueChanged(context, value) {
        var _a, _b, _c;
        const sender = (_a = context.ractive) !== null && _a !== void 0 ? _a : context;
        const tag = sender.Tag;
        tag.value = (_c = (_b = value.GetInteriorID) === null || _b === void 0 ? void 0 : _b.call(value)) !== null && _c !== void 0 ? _c : value;
        sender.ownerPanel.private_UpdatePropertyRelation(sender, tag);
    }
    private_OnSoundPlay() {
        SoundManager.tryPlaySound(this.Tag.localizationKey, true);
    }
    onBooleanSoundControlEnabledChanged(newVal) {
        void this.set('enabled', newVal);
    }
    // #region Instruments Defaults
    static onInstrumentDefaultsButtonClick(context) {
        var _a, _b;
        const sender = context.ractive;
        /* Ensure `getAllInstrumentsDefaultSettings` is defined */
        (_a = sender.getAllInstrumentsDefaultSettings) !== null && _a !== void 0 ? _a : (sender.getAllInstrumentsDefaultSettings = () => {
            const insDefSettings = new InsDefSettings();
            insDefSettings.LoadFromXML(sender.Tag.value);
            return insDefSettings;
        });
        /* Ensure `typesManagerScreenCallBack` is defined */
        (_b = sender.typesManagerScreenCallBack) !== null && _b !== void 0 ? _b : (sender.typesManagerScreenCallBack = (settings) => {
            if (!settings)
                return;
            sender.Tag.value = settings.ToXML();
        });
        /* Show the TypesManagerScreen */
        TypesManagerScreen.show(sender, null, sender.typesManagerScreenCallBack);
    }
    // #endregion
    // For public use.
    selectPage(name) {
        if (this.get('oneGroup'))
            return;
        if (!name) {
            const lastSelectedBtn = this.lastSelectedGroup;
            if (lastSelectedBtn) {
                this.selectGroup(lastSelectedBtn.get('name'));
            }
            return;
        }
        const leftPanel = this.Controls.leftPanel;
        if (!leftPanel)
            return;
        const lastSelectedBtn = this.lastSelectedGroup;
        if (lastSelectedBtn) {
            void lastSelectedBtn.set('checked', false);
        }
        const newSelectedBtn = leftPanel.Controls[name];
        if (newSelectedBtn) {
            newSelectedBtn.set('checked', true);
            this.lastSelectedGroup = newSelectedBtn;
            this.selectGroup(name);
        }
    }
    switchBtnClick(_sender, btn) {
        if (this.lastSelectedGroup && this.lastSelectedGroup !== btn) {
            this.lastSelectedGroup.set({ checked: false });
            void this.set({ isContainerScreen: false });
        }
        this.lastSelectedGroup = btn;
        this.selectGroup(btn.get('name'));
    }
    selectGroup(name) {
        this.FillGrid(this.sortedProperties[name]);
    }
    initLastBtn(name) {
        const btn = this.Controls.leftPanel.Controls[name];
        if (btn) {
            btn.set({ checked: true });
            this.lastSelectedGroup = btn;
        }
    }
    setOnClosedCallback(onClosedCallback) {
        this.onClosedCallback = onClosedCallback;
        this.on('teardown', () => {
            if (this.onClosedCallback) {
                this.onClosedCallback();
            }
            delete this.onClosedCallback;
        });
    }
    okClick() {
        var _a;
        (_a = this.get('applyProperties')) === null || _a === void 0 ? void 0 : _a(this.arrayProperties);
        this.close();
    }
    closeClick() {
        if (this.hasUnsavedChanges()) {
            TerceraMessageBox.Show(Resources.getResource('screen.properties.title'), Resources.getResource('screen.properties.unsavedChanges'), MessageBoxType.Question, () => {
                this.close();
            });
        }
        else {
            this.close();
        }
    }
    // Close button (cross icon).
    canCloseFromButton() {
        if (this.hasUnsavedChanges()) {
            this.closeClick();
            return false;
        }
        return true;
    }
    dispose() {
        var _a;
        Connection.unsubscribeOnConnectStateChange(this.onConnectionStateChange);
        (_a = this.scroll) === null || _a === void 0 ? void 0 : _a.destroy();
        super.dispose();
    }
    onCallerClosed() {
        this.close();
    }
    static editProperty(caller, headerKey, parentPanel) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(caller === null || caller === void 0 ? void 0 : caller.Properties)) {
                return;
            }
            const propScreen = caller.propertiesScreen;
            if (propScreen) {
                propScreen.gotFocus();
                return;
            }
            yield PropertySetupScreen.createPropertySetupScreenAsync(caller, headerKey, parentPanel);
        });
    }
    static createPropertySetupScreenAsync(caller, headerKey, parentPanel) {
        return __awaiter(this, void 0, void 0, function* () {
            const scr = new PropertySetupScreen();
            MainWindowManager.AddControl(scr);
            scr.PopulateProperties(caller.Properties(caller.skipSomeProperties)); // #86776 аргумент true передается для TradingCentral, указывая что нужно получить Properties только активной таблицы
            void scr.set({
                applyProperties: caller.applyProperties.bind(caller),
                header: Resources.getResource(headerKey)
            });
            scr.setOnClosedCallback(() => {
                delete caller.propertiesScreen;
                if (!isNullOrUndefined(caller.unsubscribeOnCallerClosed)) {
                    caller.unsubscribeOnCallerClosed(scr.onCallerClosed);
                }
                if (!isNullOrUndefined(caller.onClosePropertiesScreen)) {
                    caller.onClosePropertiesScreen();
                }
            });
            if (!isNullOrUndefined(caller.subscribeOnCallerClosed)) {
                caller.subscribeOnCallerClosed(scr.onCallerClosed);
            }
            caller.propertiesScreen = scr;
            scr.setParentPanel(parentPanel !== null && parentPanel !== void 0 ? parentPanel : caller);
            scr.gotFocus();
        });
    }
    hasUnsavedChanges() {
        return this.arrayProperties.some((prop, index) => {
            const oldProp = this.oldProperties[index];
            /* Compare serialized values to detect changes #124682 */
            return JSON.stringify(prop.serialize()) !== JSON.stringify(oldProp);
        });
    }
}
TerceraWindowBase.extendWith(PropertySetupScreen, {
    data: () => {
        return {
            properties: [],
            applyProperties: null,
            width: 700,
            height: 550,
            resizable: false,
            style_addition_header: 'js-PropertySetupScreen-AdditionalHeader',
            style_addition_body: 'js-PropertySetupScreen-AdditionalBody',
            style_addition_footer: 'js-PropertySetupScreen-AdditionalFooter',
            isContainerScreen: false
        };
    },
    template: TerceraBaseWindowTemplate,
    partials: {
        bodyPartial: PropertySetupScreenTemplate,
        footerPartial: PropertySetupScreenFooterTemplate
    }
});
